import { apiService } from '@/services/api';
import { SystemVersions, VersionInfo } from '@/types';
import { useQuery } from '@tanstack/react-query';

export interface UseSystemVersionsResult {
  portal: VersionInfo;
  backend: VersionInfo | null;
  forms: VersionInfo | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Portal's own version, baked in at build time - no network call needed.
 * Provides fallback values when environment variables are not available.
 *
 * @returns Portal version information with build-time metadata
 */
const getPortalVersion = (): VersionInfo => ({
  version: import.meta.env.VITE_APP_VERSION || 'dev',
  buildDate: import.meta.env.VITE_BUILD_DATE || null,
  gitCommit: import.meta.env.VITE_GIT_COMMIT || null,
  environment: import.meta.env.VITE_BUILD_ENV || 'local',
});

/**
 * Fetches backend and Nova Forms version info combined with the portal's own build-time version.
 * Displays all version information in the sidebar version dialog.
 *
 * Features:
 * - Portal version from build-time environment variables (no network call)
 * - Backend and Forms versions fetched via API with proper caching
 * - Respects enabled flag to prevent fetching when dialog is closed
 * - Handles loading and error states gracefully
 *
 * @param enabled - Whether to fetch data (typically mirrors dialog open state)
 * @returns Object containing portal, backend, and forms versions plus loading/error states
 */
export const useSystemVersions = (
  enabled: boolean = true
): UseSystemVersionsResult => {
  const query = useQuery({
    queryKey: ['systemVersions'],
    queryFn: async (): Promise<SystemVersions> => {
      const response = await apiService.getSystemVersions();
      return response.data.result;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1, // Retry once on failure
  });

  return {
    portal: getPortalVersion(),
    backend: query.data?.backend ?? null,
    forms: query.data?.forms ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
